import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./CategoryPerformanceView.module.scss";
import GenericTable from "../../../../../common/GenericTable/GenericTable";
import { isEmpty } from "lodash";
import Icon from "../../../../../common/Icon/Icon";
import { CurrencyFormatter } from "../../../../../../utils/currencyFormatter";
import { tabs } from "./constants";
import { useCategoryPerformance } from "./useCategoryPerformance";

export interface BusinessLine {
  id: string;
  name: string;
  category: string;
  revenue: number;
  bvValue: number;
  changePercentage: number;
  color?: string;
}

interface CategoryPerformanceViewProps {
  title?: string;
  description?: string;
  data?: any;
  viewType: "chart" | "table";
  showTabs?: boolean;
  highlightColumns?: number[];
  groupedColumnClass?: boolean;
  tableFallbackValue?: string;
  currency?: string;
}

const CategoryPerformanceView: React.FC<CategoryPerformanceViewProps> = ({
  title,
  description,
  data,
  viewType,
  showTabs = true,
  highlightColumns = [],
  groupedColumnClass = false,
  tableFallbackValue,
  currency = 'USD'
}) => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState(
    viewType === "chart" ? "All" : "Beauty"
  );

  const {
    top10BusinessLines,
    totalRevenue,
    restProductsData,
    tableData,
    chartData,
  } = useCategoryPerformance({
    data,
    viewType,
    selectedCategory,
  });

  const handleCategorySelection = (label: string) => {
    setSelectedCategory(label);
  };

  const ArrowIcon: React.FC<{ up: boolean }> = ({ up }) =>
    up ? (
      <Icon
        name="up_filled_arrow_table"
        size={12}
        color={"var(--tag-tag-text-green, #B3D67B)"}
        className={styles.search_icon}
      />
    ) : (
      <Icon
        name="down_filled_arrow_table"
        size={12}
        color={"var(--tag-tag-text-red, #F16A81)"}
        className={styles.search_icon}
      />
    );

  return (
    <div className={styles.PY25Revenue_wrapper}>
      {(title || description) && (
        <div className={styles.revenue_title_wrapper}>
          {title && <span className={styles.title_txt}>{title}</span>}
          {description && (
            <span className={styles.title_desc}>{description}</span>
          )}
        </div>
      )}

      {showTabs && (
        <div className={styles.pills_wrapper}>
          {tabs?.map((view: any) => (
            <div
              key={view?.viewId}
              onClick={() => handleCategorySelection(view?.label)}
              className={`${styles.view_btn} ${
                selectedCategory === view.label ? styles.selected_view : ""
              }`}
            >
              <span className={styles.label}>{view?.label}</span>
            </div>
          ))}
        </div>
      )}

      {viewType === "chart" && (
        <>
          {/* Bar Chart */}
          {top10BusinessLines.length > 0 && (
            <div className={styles.year_sales_bar_wrapper}>
              <div className={styles.year_sales_bar}>
                {top10BusinessLines.map((line) => {
                  const widthPercent =
                    totalRevenue > 0 ? (line.revenue / totalRevenue) * 100 : 0;
                  return (
                    <div
                      key={line.id}
                      className={styles.year_segment}
                      style={{
                        width: `${widthPercent}%`,
                        backgroundColor: line.color,
                      }}
                      title={`${line.name}: ${Math.round(
                        line.revenue,
                      ).toLocaleString()}`}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* List */}
          <div className={styles.products_list_wrapper}>
            {top10BusinessLines.map((line) => (
              <div key={line.id} className={styles.product_row}>
                <div className={styles.product_info}>
                  <div
                    className={styles.bullet}
                    style={{ backgroundColor: line.color }}
                  />
                  <span className={styles.product_name}>{line.name}</span>
                </div>
                <div className={styles.product_metric}>
                  <span
                    className={styles.percentage}
                    style={{
                      color:
                        line.changePercentage >= 0
                          ? "var(--tag-tag-text-green, #00A854)"
                          : "var(--tag-tag-text-red, #F03E3E)",
                    }}
                  >
                    <div className={styles.cell_with_arrow}>
                      <ArrowIcon up={line.changePercentage >= 0} />
                      {Math.abs(line.changePercentage)}%
                    </div>
                  </span>
                </div>
                <div className={styles.currency_val}>
                  {CurrencyFormatter(currency, line.revenue)}
                </div>
                <div className={styles.bv_val}>
                  {Math.round(line.bvValue).toLocaleString()} BV
                </div>
              </div>
            ))}

            {/* Rest of products row */}
            {(restProductsData.revenue > 0 || restProductsData.bvValue > 0) &&
              chartData.length > 10 && (
                <div className={styles.product_row}>
                  <div className={styles.product_info}>
                    <div
                      className={styles.bullet}
                      style={{
                        backgroundColor:
                          "var(--surface-surface-secondary, #f4f4f4)",
                      }}
                    />
                    <span className={styles.product_name}>
                      Rest all Sub Categories
                    </span>
                  </div>
                  <div className={styles.product_metric}>
                    {/* No percentage for rest usually */}
                  </div>
                  <div className={styles.currency_val}>
                    {CurrencyFormatter(currency, restProductsData.revenue)}
                  </div>
                  <div className={styles.bv_val}>
                    {Math.round(restProductsData.bvValue).toLocaleString()} BV
                  </div>
                </div>
              )}
          </div>

          {isEmpty(top10BusinessLines) && (
            <div className={styles.no_data}>{t("noDataAvailable")}</div>
          )}
        </>
      )}

      {viewType === "table" && (
        <>
          {isEmpty(tableData) ? (
            <div className={styles.no_data}>{t("noDataAvailable")}</div>
          ) : (
            <GenericTable
              tableData={tableData}
              kpiCode={"business-line"}
              highlightColumns={highlightColumns}
              groupedColumnClass={groupedColumnClass}
              emptyValuePlaceholder={tableFallbackValue}
            />
          )}
        </>
      )}
    </div>
  );
};

export default CategoryPerformanceView;
